import { Box, Typography } from "@mui/material";
import { type Dispatch, type FC, type SetStateAction, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { type HourRange, TimeRangeListField } from "../../../../components/UIToolkit/Inputs/TimeRangeListField";
import { TokenListField } from "../../../../components/UIToolkit/Inputs/TokenListField";
import { BORDERLESS_AUTOCOMPLETE_SX, BORDERLESS_PICKER_SX } from "../../../../components/UIToolkit/Tables/ConfigTable/configTableSx";
import { FONT_SIZE, INK } from "../../../../theme/tokens";
import { type ShiftTimesScope, useShiftTimesWriters } from "./shiftTimes";
import type { GeneralFormData } from "./types";

/**
 * Chrome of the panel around the two fields, so `shiftTimesEditorHeight` and the layout can't drift.
 * Heights are what the grid's fixed detail-row height is built from — see that helper.
 */
export const SHIFT_TIMES_EDITOR = {
	/**
	 * `SystemDataGrid`'s default detail-cell padding (`py: 1`), which lives INSIDE the row height the
	 * grid is given — so the height helper has to pay for it.
	 */
	GRID_DETAIL_PADDING_Y_PX: 16,
	/** The field label line above each control, plus the 2px gap under it. Measured, not guessed. */
	LABEL_ROW_PX: 20,
	/**
	 * One control: a wrapped chip line (24px) over the 40px picker pair, plus the 8px between them.
	 * Measured in the browser — the common case (chips and pickers on ONE line) is 40px and fits inside
	 * this; a job with enough ranges to need a second chip line scrolls rather than growing the row.
	 */
	FIELD_ROW_PX: 72,
	/** The hint line rendered under the fields when the row is read-only, plus its 4px gap. */
	HINT_ROW_PX: 24,
	/** Each field block's flex basis — wide enough for two time pickers, a dash and a chip. */
	FIELD_MIN_WIDTH_PX: 330,
} as const;

/** The panel's own content height, without the detail cell's padding. */
const contentHeight = (hasHint: boolean): number =>
	SHIFT_TIMES_EDITOR.LABEL_ROW_PX + SHIFT_TIMES_EDITOR.FIELD_ROW_PX + (hasHint ? SHIFT_TIMES_EDITOR.HINT_ROW_PX : 0);

/**
 * Full height (px) of a `ShiftTimesEditor`: `SystemDataGrid` sizes the detail row before the panel
 * renders, so it has to be told. One field block tall — the two fields sit side by side across the
 * grid's full scrollable width.
 *
 * The grid fixes the row height on open and cannot follow content that grows afterwards, so overrun
 * (a wrapping chip row, a validation helper) scrolls inside the panel. The panel must set this
 * height on itself too: `height: 100%` won't resolve against an auto-height detail cell, leaving the
 * overflow guard inert and the content spilling into the next row.
 */
export const shiftTimesEditorHeight = (hasHint: boolean): number => SHIFT_TIMES_EDITOR.GRID_DETAIL_PADDING_Y_PX + contentHeight(hasHint);

const FieldLabel: FC<{ children: string }> = ({ children }) => (
	<Typography component="span" sx={{ fontSize: FONT_SIZE.LABEL_XS, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: INK.MUTED }}>
		{children}
	</Typography>
);

/**
 * The two shift-time controls for ONE key — a job, a role, or the `default` bucket — laid out as a
 * labelled pair. This is the Shift Lengths table's row, lifted out of the table so it can live
 * wherever the key does: inside the Jobs grid's detail panel, or as the department-level fallback
 * card beside it (DAT-804).
 *
 * The controls and their interaction are unchanged from that table on purpose: partial lengths are
 * typed free text with a pick-list of lengths already in use, full shift times are picked as ranges.
 * Everything written here is buffered in `formData` behind the tab's single Save button.
 */
export const ShiftTimesEditor: FC<{
	scope: ShiftTimesScope;
	/** Role short code (or `default`) in role scope, job id in job scope. */
	rowKey: string;
	hours: number[];
	fullShiftTimes: HourRange[];
	/** Partial lengths configured elsewhere in the department, offered as pick-list options. */
	hourOptions: string[];
	setFormData: Dispatch<SetStateAction<GeneralFormData>>;
	/** Locks both fields and renders `hint` underneath — for a configured job the department no longer enables. */
	readOnly?: boolean;
	hint?: string;
	/**
	 * The host reserved a fixed height for this panel and cannot follow it if it grows — the grid's
	 * detail row. Caps the panel at `shiftTimesEditorHeight` and scrolls inside instead of spilling
	 * over the next row. Leave off wherever the panel can simply be as tall as its content (the
	 * fallback card), or a narrow screen that stacks the two fields would scroll a settings card.
	 */
	bounded?: boolean;
	dataTestId: string;
}> = ({ scope, rowKey, hours, fullShiftTimes, hourOptions, setFormData, readOnly = false, hint, bounded = false, dataTestId }) => {
	const { t } = useTranslation();
	const { commitPartialBlockShifts, setFullShiftTimes } = useShiftTimesWriters({ scope, rowKey, hours, setFormData });

	const partialInvalidMessage = useCallback(
		(rejected: string[]) => t("department.config.shifts.partialShiftLengths.invalid", { values: rejected.join(", ") }),
		[t],
	);

	return (
		<Box
			data-testid={dataTestId}
			sx={{
				display: "flex",
				flexDirection: "column",
				// Centred rather than top-aligned: the reserved height covers a wrapped chip row, so the
				// common one-line case would otherwise hang off the top of its own strip.
				justifyContent: "center",
				gap: 0.5,
				width: "100%",
				// `maxHeight`, never `height`: a DEFINITE height here would become the containing block for
				// any percentage-height child, and `max-height` bounds the panel without ever giving one
				// something to resolve against.
				...(bounded && { maxHeight: `${contentHeight(!!hint)}px`, overflowY: "auto" }),
			}}
		>
			<Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 4, rowGap: 1.5, alignItems: "flex-start" }}>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, flex: `1 1 ${SHIFT_TIMES_EDITOR.FIELD_MIN_WIDTH_PX}px`, minWidth: 0 }}>
					<FieldLabel>{t("department.config.shifts.col.partialShiftLengths")}</FieldLabel>
					<TokenListField
						values={hours.map((hour) => hour.toString())}
						options={hourOptions}
						onCommit={commitPartialBlockShifts}
						invalidMessage={partialInvalidMessage}
						placeholder={t("department.config.shifts.partialShiftLengths.placeholder")}
						disabled={readOnly}
						fieldSx={BORDERLESS_AUTOCOMPLETE_SX}
						dataTestId={`dept-config-shifts-lengths-partial-${rowKey}`}
					/>
				</Box>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, flex: `1 1 ${SHIFT_TIMES_EDITOR.FIELD_MIN_WIDTH_PX}px`, minWidth: 0 }}>
					<FieldLabel>{t("department.config.shifts.col.fullShiftTimes")}</FieldLabel>
					<TimeRangeListField
						value={fullShiftTimes}
						onChange={setFullShiftTimes}
						disabled={readOnly}
						fieldSx={BORDERLESS_PICKER_SX}
						dataTestId={`dept-config-shifts-lengths-full-${rowKey}`}
					/>
				</Box>
			</Box>
			{hint && <Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }}>{hint}</Typography>}
		</Box>
	);
};
